/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 24, 2026 by hoarfroster
 *
 */

import { nanoid } from '@utils/crypto';
import { GoodDate } from '@utils/datetime';
import httpRequest from '@utils/http-request';
import type { PasskeyJSON } from '@models/user/passkey.ts';
import { asRecord, asString, asNumber, asArray, asNullableNumber, asBoolean } from '@models/json-utils';

export enum UserRole {
    UserVisitor = -1,
    User = 0,
    Admin = 1,
    SuperAdmin = 2,
}

// The low byte of the role integer stores the global role. Module-local ACL
// bits start above it so permissions remain in the existing role field.
export const UserRoleMask = 0xff;
export const UserRoleMax = 2 ** 31 - 1;

export enum CookingPermission {
    None = 0,
    Edit = 1 << 8,
}

export type UserRoleValue = number;

export function getBaseUserRole(role: number): UserRole {
    const numericRole = Number(role);
    if (!Number.isSafeInteger(numericRole) || numericRole < 0 || numericRole > UserRoleMax) {
        return UserRole.UserVisitor;
    }
    return Number(BigInt(Math.trunc(numericRole)) & BigInt(UserRoleMask));
}

export function isUserRole(role: number, expected: UserRole): boolean {
    return getBaseUserRole(role) === expected;
}

export function hasUserRolePermission(role: number, permission: CookingPermission): boolean {
    const numericRole = Number(role);
    const numericPermission = Number(permission);
    if (
        !Number.isSafeInteger(numericRole) ||
        numericRole < 0 ||
        !Number.isSafeInteger(numericPermission) ||
        numericPermission <= 0 ||
        (BigInt(numericPermission) & BigInt(UserRoleMask)) !== 0n
    ) {
        return false;
    }
    return (BigInt(numericRole) & BigInt(numericPermission)) === BigInt(numericPermission);
}

export function updateUserRolePermission(role: number, permission: CookingPermission, enabled: boolean): number {
    const numericRole = Number.isSafeInteger(Number(role)) ? Math.trunc(Number(role)) : UserRole.User;
    if (getBaseUserRole(numericRole) === UserRole.UserVisitor) return UserRole.UserVisitor;
    const numericPermission = Number(permission);
    if (
        !Number.isSafeInteger(numericPermission) ||
        numericPermission <= 0 ||
        (BigInt(numericPermission) & BigInt(UserRoleMask)) !== 0n
    ) {
        return numericRole;
    }
    const value = enabled
        ? BigInt(numericRole) | BigInt(numericPermission)
        : BigInt(numericRole) & ~BigInt(numericPermission);
    return Number(value);
}

export function updateUserBaseRole(role: number, baseRole: UserRole): number {
    const numericRole = Number.isSafeInteger(Number(role)) && Number(role) >= 0 ? BigInt(Math.trunc(Number(role))) : 0n;
    const permissionBits = numericRole & ~BigInt(UserRoleMask);
    return Number(permissionBits | BigInt(baseRole));
}

export function hasMinimumUserRole(role: number, minimum: UserRole): boolean {
    return getBaseUserRole(role) >= minimum;
}

export enum TimeZone {
    TimeZoneHongKong = 0,
    TimeZoneBeijing,
    TimeZoneShanghai,
    TimeZoneTokyo,
    TimeZoneSeoul,
    TimeZoneKualaLumpur,
    TimeZoneSingapore,
    TimeZoneJakarta,
    TimeZoneMumbai,
    TimeZoneAuckland,
    TimeZoneSydney,
    TimeZonePacific,
    TimeZoneNewYork,
    TimeZoneLondon,
    TimeZoneParis,
    TimeZoneMoscow,
}

export interface AdminUserSummary {
    id: string;
    name: string;
    email: string;
    role: UserRoleValue;
    location: string;
    timeZone: TimeZone;
    bio: string;
    socialAccounts: string[];
    contactId?: string | null;
    contact?: {
        id?: string;
        firstName?: string;
        lastName?: string;
        nickname?: string;
    } | null;
    createdAt?: number | null;
    updatedAt?: number | null;
    otpEnabled: boolean;
}

export interface AdminUpdateUserProfilePayload {
    name?: string;
    email?: string;
    role?: UserRoleValue;
    location?: string;
    timeZone?: TimeZone;
    bio?: string;
    socialAccounts?: string[];
    contactId?: string | null;
}

export interface AdminCreateUserPayload {
    name: string;
    email: string;
    password: string;
    role?: UserRoleValue;
    location?: string;
    timeZone?: TimeZone;
    bio?: string;
    socialAccounts?: string[];
    contactId?: string | null;
}

export interface TOTPUpdateResponse {
    otpEnabled: boolean;
    token?: string;
    user?: User;
}

export interface SignInSuccess {
    type: 'success';
    token: string;
    user: User;
}

export interface SignInTOTPRequired {
    type: 'totp-required';
    message: string;
    totpChallenge: string;
}

export type SignInResult = SignInSuccess | SignInTOTPRequired;

// Formerly had a strict UserJSON shape here; model parsing now accepts unknown and uses runtime checks

export interface UpdateUserProfilePayload {
    name?: string;
    email?: string;
    location?: string;
    timeZone?: TimeZone;
    bio?: string;
    socialAccounts?: string[];
    contactId?: string | null;
}
const API_BASE = import.meta.env.VITE_SERVER_ENDPOINT;

export class User {
    id: string;
    name: string;
    email: string;
    role: UserRoleValue;
    password: string;
    otpSalt: string;
    otpEnabled: boolean;
    avatar: string;
    bio: string;
    location: string;
    socialAccounts: string[];
    timeZone: number;
    createdAt: GoodDate;
    updatedAt: GoodDate;
    deletedAt?: GoodDate;
    passkeys?: PasskeyJSON[];
    microsoftEmail?: string | null;
    microsoftBound?: boolean;

    constructor(
        id: string,
        name: string,
        email: string,
        role: UserRoleValue,
        password: string,
        otpSalt: string,
        avatar: string,
        bio: string,
        location: string,
        timeZone: TimeZone,
        socialAccounts: string[],
        createdAt: number,
        updatedAt: number,
        deletedAt?: number,
        passkeys?: PasskeyJSON[],
        microsoftEmail?: string | null,
        microsoftBound?: boolean,
        otpEnabled = false
    ) {
        this.id = id;
        this.name = name;
        this.email = email;
        this.role = role;
        this.password = password;
        this.otpSalt = otpSalt;
        this.otpEnabled = otpEnabled;
        this.avatar = avatar;
        this.bio = bio;
        this.location = location;
        this.timeZone = timeZone;
        this.socialAccounts = socialAccounts;
        this.createdAt = GoodDate.fromEpochSeconds(createdAt);
        this.updatedAt = GoodDate.fromEpochSeconds(updatedAt);
        this.deletedAt = deletedAt ? GoodDate.fromEpochSeconds(deletedAt) : undefined;
        this.passkeys = passkeys;
        this.microsoftEmail = microsoftEmail ?? null;
        this.microsoftBound = microsoftBound ?? false;
    }

    static fromJSON(json: unknown): User {
        const j = asRecord(json);
        return new User(
            asString(j.id),
            asString(j.name),
            asString(j.email),
            Number(j.role) || 0,
            asString(j.password) ?? '',
            asString(j.otpSalt) ?? '',
            asString(j.avatar) ?? '',
            asString(j.bio),
            asString(j.location),
            Number(j.timeZone) || 0,
            Array.isArray(j.socialAccounts) ? (j.socialAccounts as unknown[]).map((s) => String(s)) : [],
            asNumber(j.createdAt),
            asNumber(j.updatedAt),
            asNullableNumber(j.deletedAt) ?? undefined,
            (j.passkeys as PasskeyJSON[]) ?? undefined,
            (j.microsoftEmail as string) ?? null,
            (j.microsoftBound as boolean) ?? false,
            asBoolean(j.otpEnabled, Boolean(asString(j.otpSalt)))
        );
    }

    static fromJSONList(json: unknown): User[] {
        return asArray(json, (item) => User.fromJSON(item));
    }

    static template = new User(
        nanoid(12),
        '',
        '',
        0,
        '',
        '',
        '',
        '',
        '',
        0,
        [],
        GoodDate.nowInEpochSeconds(),
        GoodDate.nowInEpochSeconds(),
        undefined,
        undefined,
        null,
        false,
        false
    );

    static roles = ['Visitor', 'User', 'Admin', 'Super Admin'];

    static list = async () => {
        const response = await httpRequest(`${API_BASE}/user/user`, 'GET'),
            json = await response.json();
        if (response.ok) {
            return User.fromJSONList(json.data);
        } else {
            throw new Error(json.message);
        }
    };

    static signIn = async (data: {
        name?: string;
        password?: string;
        otpCode?: string;
        totpChallenge?: string;
    }): Promise<SignInResult> => {
        const response = await httpRequest(`${API_BASE}/user/user/sign-in`, 'POST', data),
            json = await response.json();

        if (json.code === 435011 && typeof json.data?.totpChallenge === 'string') {
            return {
                type: 'totp-required',
                message: asString(json.message, 'TOTP verification required'),
                totpChallenge: json.data.totpChallenge,
            };
        }

        if (response.ok && json.data !== undefined && json.data !== null) {
            const data = json.data;
            return { type: 'success', token: data.token as string, user: User.fromJSON(data.user) };
        } else {
            throw new Error(json.message);
        }
    };

    static validate = async (): Promise<User> => {
        const response = await httpRequest(`${API_BASE}/user/user/validate`, 'GET'),
            json = await response.json();

        if (response.status === 200) {
            return User.fromJSON(json.data);
        } else {
            throw new Error(json.message);
        }
    };

    static getByID = async (id: string): Promise<User> => {
        const response = await httpRequest(`${API_BASE}/user/user/byId/${id}`, 'GET'),
            json = await response.json();

        if (response.status == 200) {
            return User.fromJSON(json.data);
        } else {
            throw new Error(json.message);
        }
    };

    static getByName = async (name: string): Promise<User> => {
        const response = await httpRequest(`${API_BASE}/user/user/byName/${name}`, 'GET'),
            json = await response.json();

        if (response.status == 200) {
            return User.fromJSON(json.data);
        } else {
            throw new Error(json.message);
        }
    };

    static updateProfile = async (userID: string, data: UpdateUserProfilePayload): Promise<User> => {
        const response = await httpRequest(`${API_BASE}/user/user/byId/${userID}`, 'PUT', data),
            json = await response.json();

        if (response.status === 200) {
            return User.fromJSON(json.data);
        } else {
            throw new Error(json.message);
        }
    };

    static updateTOTPSecret = async (secret: string | null): Promise<TOTPUpdateResponse> => {
        const response = await httpRequest(`${API_BASE}/user/user/totp`, 'POST', {
            totpSecret: secret,
        });
        const json = await response.json();
        if (response.ok) {
            const user = json.data?.user ? User.fromJSON(json.data.user) : undefined;
            return {
                otpEnabled: asBoolean(json.data?.otpEnabled, user?.otpEnabled ?? false),
                token: typeof json.data?.token === 'string' ? json.data.token : undefined,
                user,
            };
        }
        throw new Error(json.message ?? 'Failed to update TOTP');
    };

    static updatePassword = async (data: { name: string; oldPassword: string; newPassword: string }): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/user/user/update-password`, 'POST', data);
        if (response.status === 200) {
            return;
        }
        const json = await response.json();
        throw new Error(json.message);
    };

    static register = async (data: {
        name: string;
        email: string;
        password: string;
        code: string;
    }): Promise<{ token: string; user: User }> => {
        const response = await httpRequest(`${API_BASE}/user/user/register`, 'POST', data),
            json = await response.json();

        if (response.status === 201 && json.data) {
            const d = json.data;
            return { token: d.token as string, user: User.fromJSON(d.user) };
        } else {
            throw new Error(json.message);
        }
    };

    static saveTOTPSecret = async (secret: string | null): Promise<TOTPUpdateResponse> => {
        const response = await httpRequest(`${API_BASE}/user/user/totp`, 'POST', {
            totpSecret: secret,
        });
        const json = await response.json();
        if (response.ok) {
            const user = json.data?.user ? User.fromJSON(json.data.user) : undefined;
            return {
                otpEnabled: asBoolean(json.data?.otpEnabled, user?.otpEnabled ?? false),
                token: typeof json.data?.token === 'string' ? json.data.token : undefined,
                user,
            };
        }
        throw new Error(json.message ?? 'Failed to update TOTP');
    };

    static uploadAvatar = async (file: File): Promise<boolean> => {
        const response = await httpRequest(`${API_BASE}/user/user/avatar`, 'POST', file, false);

        return response.status === 200;
    };

    static adminList = async (): Promise<AdminUserSummary[]> => {
        const response = await httpRequest(`${API_BASE}/user/user/admin/users`, 'GET');
        const json = await response.json();
        if (response.ok) {
            return json.data ?? [];
        }
        throw new Error(json.message ?? json.error ?? 'Failed to load users');
    };

    static adminUpdateProfile = async (
        userID: string,
        payload: AdminUpdateUserProfilePayload
    ): Promise<AdminUserSummary> => {
        const body: Record<string, unknown> = { ...payload };
        if (body.contactId === null) {
            body.contactId = '';
        }
        const response = await httpRequest(`${API_BASE}/user/user/admin/users/${userID}/profile`, 'PATCH', body);
        const json = await response.json();
        if (response.ok) {
            return json.data;
        }
        throw new Error(json.message ?? json.error ?? 'Failed to update user profile');
    };

    static adminUpdatePassword = async (userID: string, newPassword: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/user/user/admin/users/${userID}/password`, 'POST', {
            newPassword,
        });
        if (!response.ok) {
            const json = await response.json();
            throw new Error(json.message ?? json.error ?? 'Failed to update password');
        }
    };

    static adminUpdateTOTP = async (userID: string, totpSecret: string | null): Promise<TOTPUpdateResponse> => {
        const response = await httpRequest(`${API_BASE}/user/user/admin/users/${userID}/totp`, 'POST', {
            totpSecret,
        });
        const json = await response.json();
        if (response.ok) {
            const user = json.data?.user ? User.fromJSON(json.data.user) : undefined;
            return {
                otpEnabled: asBoolean(json.data?.otpEnabled, user?.otpEnabled ?? false),
                token: typeof json.data?.token === 'string' ? json.data.token : undefined,
                user,
            };
        }
        throw new Error(json.message ?? json.error ?? 'Failed to update TOTP');
    };

    static adminCreate = async (payload: AdminCreateUserPayload) => {
        const response = await httpRequest(`${API_BASE}/user/user/admin/users`, 'POST', payload);
        const json = await response.json();
        if (response.ok) {
            return json.data as AdminUserSummary;
        }
        throw new Error(json.message ?? json.error ?? 'Failed to create user');
    };

    static adminDelete = async (userID: string) => {
        const response = await httpRequest(`${API_BASE}/user/user/admin/users/${userID}`, 'DELETE');
        if (!response.ok) {
            const json = await response.json();
            throw new Error(json.message ?? json.error ?? 'Failed to delete user');
        }
    };
}
